import {Injectable} from "@angular/core";
import {Storage} from "@ionic/storage-angular";
import * as CordovaSQLiteDriver from "localforage-cordovasqlitedriver";
import {ScreenOrientationService} from "./screen-orientation.service";
import {TranslateService} from "@ngx-translate/core";
import { BehaviorSubject, Observable } from "rxjs";
import { Logger, LoggingService } from "ionic-logging-service";
import { RestolutionService } from "./restolution";
import { Discount } from "../types/discount";

@Injectable({
  providedIn: "root"
})
export class StorageService {

  public static readonly orientationKey = "orientation";
  public static readonly languageKey = "language";
  public static readonly receiptNumberKey = "receiptNumber";

  public clerkID;
  public discounts: Discount[] = [];

  private storage: Storage | null = null;
  private language = new BehaviorSubject<any>({ language: "en" });
  // eslint-disable-next-line @typescript-eslint/member-ordering
  languageStream = this.language.asObservable();

  private logger: Logger;

  constructor(private initStorage: Storage, private translate: TranslateService, private loggingService: LoggingService) {
    this.logger = loggingService.getLogger("BestoGo.StorageService");
  }

  async init() {
    await this.initStorage.defineDriver(CordovaSQLiteDriver);
    const storage = await this.initStorage.create();
    this.storage = storage;

    const clerkID = await this.storage.get("clerkID");
    if (clerkID) {
      this.clerkID = clerkID;
    }

    const discounts = await this.storage.get("discounts");
    if (discounts) {
      this.discounts = discounts;
    }
  }

  public set(key: string, value: any) {
    this.storage?.set(key, value);
    if (key === StorageService.languageKey) {
      this.handleLanguageChange(value);
    }
  }

  public setClerkID(value: any) {
    this.clerkID = value;
    this.storage.set("clerkID", value);
  }

  public setDiscounts(currentDiscount: Discount, mode: string) {
    if (mode === "add") {
      this.discounts = [...this.discounts, currentDiscount];
    } else if (mode === "delete") {
      this.discounts = this.discounts.filter((discount: Discount) => discount.name !== currentDiscount.name );
    } else if (mode === "edit") {
      this.discounts = this.discounts.map((discount: Discount) => discount.name === currentDiscount.name ? currentDiscount : discount);
    }
    this.storage.set("discounts", this.discounts);
  }

  public async get(key: string) {
    if (!this.storage) {
      await this.init();
    }
    let retrievedData;
    try {
      retrievedData = await this.storage.get(key);
    } catch (e){
      retrievedData = null;
      this.logger.error("get", "Error: ", e);
    }
    return retrievedData;
  }

  public async getAllKeys() {
    if (!this.storage) {
      await this.init();
    }
    let retrievedKeys;
    try {
      retrievedKeys = await this.storage.keys();
    } catch {
      retrievedKeys = null;
    }
    return retrievedKeys;
  }

  async initLanguage(): Promise<string> {
    if (!this.storage) {
      await this.init();
    }
    let curLang = await this.storage.get(StorageService.languageKey);
    if (curLang !== null) {
      this.handleLanguageChange(curLang);
      return curLang;
    } else {
      curLang = "fi";
      this.handleLanguageChange(curLang);
      this.set(StorageService.languageKey, curLang);
      return curLang;
    }
  }

 /*  private handleOrientationChange(orientationValue: string) {
    this.orientationService.lock(orientationValue);
  } */

  private handleLanguageChange(selectedLanguage: string) {
    this.translate.use(selectedLanguage);
    this.language.next({ language: selectedLanguage });
  }

}
